import { DEFAULT_CURRENCY, DEFAULT_LANDING_PAGE_ACCENT_COLOR } from '@consts';
import PercentDiscountIcon from '@icons/PercentDiscountIcon';
import SuccessCircleIcon from '@icons/SuccessCircleIcon';
import TickIcon from '@icons/TickIcon';
import { setupForDark } from '@theme';
import classNames from 'classnames';
import { GoalSellingStatus } from 'lib/models/goals';
import moment from 'moment';
import { ReactNode } from 'react';
import { usePaymentAndOrderOptional } from '../../payments/PaymentAndOrderContext';
import GoalLandingPageMainCTAButton from './GoalLandingPageMainCTAButton';
import { parseHighlightedText } from './highlighters';
import { useAccentSpotlight } from './useAccentSpotlight';
import { useLandingPageTranslation } from './useLandingPageTranslation';

const formatPrice = (price: number) => {
  return Math.floor(price / 100).toString();
};

const formatReleaseDate = (utcDate: string | Date) => {
  return moment.utc(utcDate).local().format('D MMM YYYY');
};

interface PricingContent {
  title?: string | null;
  description?: string | null;
  benefits?: string[];
  includedTitle?: string | null;
  included?: string[];
}

interface PricingLabels {
  benefitsTitle?: string;
  onlineCourse?: string;
  whatIncludedTitle?: string;
  forFree?: string;
}

interface GoalLandingPagePricingSectionProps {
  pricing?: PricingContent;
  accentColor?: string;
  currentPriceAmount?: number;
  currency?: string;
  priceSuffix?: string;
  labels?: PricingLabels;
  cta?: ReactNode;
}

export default function GoalLandingPagePricingSection({
  pricing: pricingProp,
  accentColor: accentColorProp,
  currentPriceAmount: currentPriceAmountProp,
  currency = DEFAULT_CURRENCY,
  priceSuffix,
  labels,
  cta
}: GoalLandingPagePricingSectionProps = {}) {
  const ctx = usePaymentAndOrderOptional();
  const goal = ctx?.state.goal;
  const { t } = useLandingPageTranslation();

  const accentColor =
    accentColorProp ?? goal?.landingPage?.accentColor ?? DEFAULT_LANDING_PAGE_ACCENT_COLOR;
  const pricing = pricingProp ?? goal?.landingPage?.pricing ?? undefined;
  const spotlight = useAccentSpotlight(accentColor);

  const regularPriceAmount = goal?.landingPage?.pricing?.regularPrice?.amount || 0;
  const currentPriceAmount = currentPriceAmountProp ?? goal?.currentPrice?.amount ?? 0;

  const hasRegularPrice = regularPriceAmount > currentPriceAmount;
  const priceDifference = regularPriceAmount - currentPriceAmount;
  const displayedRegular = Math.floor(regularPriceAmount / 100);
  const displayedCurrent = Math.floor(currentPriceAmount / 100);
  const savedAmount = hasRegularPrice ? displayedRegular - displayedCurrent : 0;
  const savedPercentage = hasRegularPrice
    ? Math.round((priceDifference / (regularPriceAmount || 1)) * 100)
    : 0;

  const isPreSale = goal?.sellingStatus === GoalSellingStatus.PRE_SALE;
  const shouldShowRegularPrice = isPreSale && hasRegularPrice;

  const regularPriceFormatted = formatPrice(regularPriceAmount);
  const currentPriceFormatted = formatPrice(currentPriceAmount);

  const benefitsTitle =
    labels?.benefitsTitle ?? t('domains.goals.landingPage.pricing.benefitsTitle');
  const onlineCourseLabel =
    labels?.onlineCourse ?? t('domains.goals.landingPage.pricing.onlineCourse');
  const whatIncludedTitle =
    labels?.whatIncludedTitle ?? t('domains.goals.landingPage.pricing.whatIncludedTitle');
  const forFreeLabel = labels?.forFree ?? t('domains.goals.landingPage.pricing.forFree');

  return (
    <div
      id='pricing'
      className={classNames(
        'w-full flex flex-col items-center bg-gradient-to-r from-landing-hero-from to-landing-hero-to',
        // RESPONSIVE STYLES
        'p-sm @[768px]:p-md @[1280px]:py-xxxl @[1280px]:px-none'
      )}
    >
      <div
        className={classNames(
          'w-full max-w-[1200px] flex gap-xl justify-between',
          // RESPONSIVE STYLES
          'flex-col @[768px]:flex-row'
        )}
      >
        <div
          className={classNames(
            'flex flex-col gap-huge',
            // RESPONSIVE STYLES
            '@[768px]:max-w-[600px] @[1280px]:w-[600px]'
          )}
        >
          <div className={classNames('flex flex-col gap-xl', setupForDark('text-white'))}>
            <div
              className={classNames(
                'font-bold leading-[1.1]',
                // RESPONSIVE STYLES
                'text-center @[768px]:text-left text-[32px] @[480px]:text-[48px] @[640px]:text-[60px] break-words'
              )}
            >
              {parseHighlightedText(pricing?.title, 'text-white', undefined, accentColor)}
            </div>
            <div
              className={classNames(
                'P2R',
                // RESPONSIVE STYLES
                'text-center @[768px]:text-left @[640px]:text-[20px]'
              )}
            >
              {parseHighlightedText(pricing?.description, 'text-white', undefined, accentColor)}
            </div>
          </div>

          <div className='flex flex-col gap-md'>
            <div
              className={classNames(
                'H5',
                setupForDark('text-white'),
                // RESPONSIVE STYLES
                'text-center @[768px]:text-left'
              )}
            >
              {benefitsTitle}
            </div>
            <div
              className={classNames(
                'flex flex-col gap-xs w-full',
                // RESPONSIVE STYLES
                'px-lg @[768px]:px-none',
                'mx-auto max-w-[480px] @[768px]:max-w-none'
              )}
            >
              {pricing?.benefits?.map((benefit, index) => (
                <div key={index} className='flex gap-xs items-center'>
                  <SuccessCircleIcon size={24} style={{ color: accentColor }} />
                  <div
                    className={classNames(
                      'P2R flex-1',
                      setupForDark('text-white'),
                      // RESPONSIVE STYLES
                      '@[640px]:text-[20px]'
                    )}
                  >
                    {parseHighlightedText(benefit, 'text-white', undefined, accentColor)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div
          id='pricing-card'
          ref={spotlight.ref}
          style={spotlight.style}
          className={classNames(
            'rounded-lg flex flex-col self-center @[768px]:self-start',
            spotlight.className,
            // RESPONSIVE STYLES
            'max-w-[550px] @[1280px]:w-[550px] @[640px]:min-w-[464px]',
            setupForDark('bg-white')
          )}
        >
          <div
            className={classNames(
              'flex flex-col gap-xl',
              // RESPONSIVE STYLES
              'p-md @[768px]:p-xxxl @[1280px]:p-[40px]'
            )}
          >
            <div
              className='P2B px-sm py-xs rounded-md w-fit items-center self-center @[768px]:self-start'
              style={{ color: accentColor, backgroundColor: `${accentColor}1A` }}
            >
              {onlineCourseLabel}
            </div>
            <div
              className={classNames(
                'font-bold leading-[1.1]',
                // RESPONSIVE STYLES
                'text-center @[768px]:text-left text-[28px] @[480px]:text-[32px] @[640px]:text-[48px] break-words'
              )}
            >
              {parseHighlightedText(pricing?.includedTitle, 'text-black', undefined, accentColor)}
            </div>

            <div className={classNames('flex flex-col gap-sm')}>
              <div
                className={classNames(
                  'P2B',
                  setupForDark('text-black'),
                  // RESPONSIVE STYLES
                  'text-center @[768px]:text-left @[640px]:text-[20px]'
                )}
              >
                {whatIncludedTitle}
              </div>
              <div className='flex flex-col gap-xs mx-auto max-w-[480px] @[768px]:max-w-none'>
                {pricing?.included?.map((included, index) => (
                  <div key={index} className='flex gap-xs items-center'>
                    <TickIcon size={24} style={{ color: accentColor }} />
                    <div
                      className={classNames(
                        'P2R flex-1',
                        setupForDark('text-black'),
                        // RESPONSIVE STYLES
                        '@[640px]:text-[20px]'
                      )}
                    >
                      {parseHighlightedText(included, 'text-black', undefined, accentColor)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Price  */}
          {isPreSale ? (
            <>
              <div
                className={classNames(
                  'bg-gradient-to-r from-landing-gradient-green-from to-landing-gradient-green-to',
                  'px-sm py-md flex flex-col gap-xs',
                  'text-center @[768px]:text-left',
                  setupForDark('text-white')
                )}
              >
                <div className='P1B uppercase flex flex-row gap-xs items-center justify-center @[768px]:justify-start'>
                  <PercentDiscountIcon size={20} /> {t('domains.goals.sellingStatus.preSale')}
                </div>
                <div className='P2R'>{t('domains.goals.landingPage.pricing.preSaleCTA')}</div>
                <div className='P2R pt-md'>
                  {t('domains.goals.landingPage.pricing.startDate')}{' '}
                  <span className='font-bold'>
                    {goal?.releaseDate && formatReleaseDate(goal.releaseDate)}
                  </span>
                </div>
              </div>
              <div
                className={classNames(
                  // RESPONSIVE STYLES
                  'text-center @[768px]:text-left',
                  'p-md @[768px]:p-xxxl @[1280px]:p-[40px]'
                )}
              >
                <div className=''>
                  <span
                    className={classNames(
                      'font-bold leading-[1.2]',
                      setupForDark('text-black'),
                      // RESPONSIVE STYLES
                      'text-[28px] @[640px]:text-[40px]'
                    )}
                  >
                    {shouldShowRegularPrice && (
                      <span
                        className={classNames(setupForDark('text-icon-secondary'), 'line-through')}
                      >
                        {regularPriceFormatted} {DEFAULT_CURRENCY}
                      </span>
                    )}{' '}
                    {currentPriceFormatted} {DEFAULT_CURRENCY}{' '}
                  </span>
                  <span className='H6'>{t('domains.goals.landingPage.pricing.course')}</span>
                </div>
                {shouldShowRegularPrice && (
                  <div className={classNames('P1R')}>
                    {t('domains.goals.landingPage.pricing.youSave', {
                      amount: savedAmount,
                      percentage: savedPercentage
                    })}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div
              className={classNames(
                'flex items-end gap-xs',
                // RESPONSIVE STYLES
                'justify-center @[768px]:justify-start',
                'px-md @[768px]:px-xxxl @[1280px]:px-[40px]',
                'pb-md @[768px]:pb-xxxl @[1280px]:pb-[40px]'
              )}
            >
              <span
                className={classNames(
                  'font-bold leading-[1.2]',
                  setupForDark('text-black'),
                  // RESPONSIVE STYLES
                  'text-[32px] @[640px]:text-[40px]'
                )}
              >
                {currentPriceAmount === 0 ? forFreeLabel : `${currentPriceFormatted} ${currency}`}
              </span>
              {priceSuffix && currentPriceAmount !== 0 && (
                <span className={classNames('H6', setupForDark('text-black'))}>{priceSuffix}</span>
              )}
            </div>
          )}

          {/* CTA Button — anchor for #pricing-cta */}
          <div
            id='pricing-cta'
            className={classNames(
              // RESPONSIVE STYLES
              'text-center @[768px]:text-left',
              'px-md @[768px]:px-xxxl @[1280px]:px-[40px]',
              'pb-md @[768px]:pb-xxxl @[1280px]:pb-[40px]'
            )}
          >
            {cta ?? <GoalLandingPageMainCTAButton />}
          </div>
        </div>
      </div>
    </div>
  );
}
